import { createApp } from "vue";
import App from "@/App.vue";
import router from "@/router";
import store from "@/store/store";
import i18n from "@/locales";
import date from "@/filters/date";
import amountFormatter from "@/filters/amountFormatter";
import numberFormatter from "@/filters/numberFormatter";
import api from "@/backend/api";
import VueGoogleMaps from "@fawmi/vue-google-maps";

const meta = document.querySelector('meta[name="csp-nonce"]');
const NONCE = meta ? meta.content : "";

// Patch document.write and writeln (for Adobe Launch and similar)
(() => {
  const origWrite = document.write;
  const origWriteln = document.writeln;
  const addNonce = (html) => (typeof html === "string" ? html.replace(/<(script|style)(\s[^>]*)?>/gi, (m, tag, attrs = "") => `<${tag}${attrs} nonce="${NONCE}">`) : html);
  document.write = function (html) {
    return origWrite.call(document, addNonce(html));
  };
  document.writeln = function (html) {
    return origWriteln.call(document, addNonce(html));
  };
})();

// Patch insertAdjacentHTML
const origInsertAdj = Element.prototype.insertAdjacentHTML;
Element.prototype.insertAdjacentHTML = function (pos, html) {
  if (NONCE && ["beforeend", "afterbegin"].includes(pos)) {
    html = html.replace(/<(script|style)(\s[^>]*)?>/gi, (m, tag, attrs = "") => `<${tag}${attrs} nonce="${NONCE}">`);
  }
  return origInsertAdj.call(this, pos, html);
};

//  Patch innerHTML setter
const desc = Object.getOwnPropertyDescriptor(Element.prototype, "innerHTML");
Object.defineProperty(Element.prototype, "innerHTML", {
  set(html) {
    if (NONCE && typeof html === "string") {
      html = html.replace(/<(script|style)(\s[^>]*)?>/gi, (m, tag, attrs = "") => `<${tag}${attrs} nonce="${NONCE}">`);
    }
    desc.set.call(this, html);
  },
  get() {
    return desc.get.call(this);
  },
});

// Patch element insertion methods
["appendChild", "insertBefore"].forEach((method) => {
  const orig = Node.prototype[method];
  Node.prototype[method] = function (child, ref) {
    if (NONCE && child instanceof Element && (child.tagName === "SCRIPT" || child.tagName === "STYLE" || (child.tagName === "LINK" && child.rel === "stylesheet"))) {
      child.setAttribute("nonce", NONCE);
    }
    return orig.call(this, child, ref);
  };
});

// Patch createElement and createElementNS
const origCreate = document.createElement;
document.createElement = function (tag, opts) {
  const el = origCreate.call(this, tag, opts);
  if (NONCE && ["script", "style", "link"].includes(tag.toLowerCase())) {
    el.setAttribute("nonce", NONCE);
  }
  return el;
};
const origCreateNS = document.createElementNS;
document.createElementNS = function (ns, tag, opts) {
  const el = origCreateNS.call(this, ns, tag, opts);
  if (NONCE && ["script", "style", "link"].includes(tag.toLowerCase())) {
    el.setAttribute("nonce", NONCE);
  }
  return el;
};

const app = createApp(App);

app.config.globalProperties.window = window;
app.config.globalProperties._satellite = globalThis.window?._satellite;
app.config.globalProperties.$filters = {
  formatDate: date,
  formatAmount: amountFormatter,
  formatNumber: numberFormatter,
};
app.use(store);
app.config.globalProperties.$api = api;
app.provide("api", api);
app.use(router);
app.use(i18n);
app.use(VueGoogleMaps, {
  load: {
    key: import.meta.env.VITE_GOOGLE_MAPS_KEY,
  },
});
// Enable Vue devtools
if (import.meta.env.NODE_ENV !== "production") {
  app.config.devtools = true;
}
app.mount("#app");
